import { config } from '../config';
import { PATH_SEGMENTS, apiEndpoint } from '../config/paths';
import type { LiveAssistSessionPayload } from './liveAssistClient';

const api = apiEndpoint(config.api.baseUrl);
const demos = (...parts: (string | number)[]) => api(PATH_SEGMENTS.PUBLIC_DEMOS, ...parts);

export interface DemoCatalogEntry {
  id: string;
  title: string;
  industry: string;
  country: string;
  languages: string[];
  featured: boolean;
  sort_order: number;
  agent: { display_name?: string; tagline?: string; [key: string]: unknown };
  summary: string;
  steps: string[];
}

type Envelope<T> = { message: string; data: T };

async function publicRequest<T>(
  url: string,
  options: RequestInit = {},
  demoToken?: string | null
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };
  if (demoToken) {
    headers.Authorization = `Bearer ${demoToken}`;
  }
  const response = await fetch(url, { ...options, headers });
  if (!response.ok) {
    let message = `HTTP ${response.status}`;
    try {
      const body = await response.json();
      message = body.message || body.detail || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  return (await response.json()) as T;
}

export async function listPublicDemos(): Promise<DemoCatalogEntry[]> {
  const res = await publicRequest<Envelope<{ demos: DemoCatalogEntry[] }>>(demos());
  return res.data.demos || [];
}

export async function getPublicDemo(demoId: string): Promise<DemoCatalogEntry> {
  const res = await publicRequest<Envelope<{ demo: DemoCatalogEntry }>>(demos(demoId));
  return res.data.demo;
}

export async function createPublicDemoSession(demoId: string): Promise<{
  demo_id: string;
  access_token: string;
  token_type: string;
  expires_in: number;
  session: LiveAssistSessionPayload & { media_ws_path?: string; demo_id?: string };
  turn?: number;
  result?: Record<string, unknown>;
}> {
  const res = await publicRequest<
    Envelope<{
      demo_id: string;
      access_token: string;
      token_type: string;
      expires_in: number;
      session: LiveAssistSessionPayload & { media_ws_path?: string; demo_id?: string };
      turn?: number;
      result?: Record<string, unknown>;
    }>
  >(demos(demoId, 'sessions'), { method: 'POST', body: JSON.stringify({ input_source_type: 'audio_mic' }) });
  return res.data;
}

export async function publicDemoStartAudio(
  sessionId: string,
  demoToken: string
): Promise<{
  media_ws_path?: string;
  turn?: number;
  result?: Record<string, unknown>;
  session?: LiveAssistSessionPayload;
}> {
  const res = await publicRequest<
    Envelope<{
      media_ws_path?: string;
      turn?: number;
      result?: Record<string, unknown>;
      session?: LiveAssistSessionPayload;
    }>
  >(demos('sessions', sessionId, 'audio', 'start'), { method: 'POST', body: '{}' }, demoToken);
  return res.data;
}

export async function publicDemoProcessTurn(
  sessionId: string,
  demoToken: string,
  utterance: string
): Promise<{
  session: LiveAssistSessionPayload;
  turn: number;
  result: Record<string, unknown>;
}> {
  const res = await publicRequest<
    Envelope<{ session: LiveAssistSessionPayload; turn: number; result: Record<string, unknown> }>
  >(
    demos('sessions', sessionId, 'turns'),
    { method: 'POST', body: JSON.stringify({ speaker: 'customer', utterance }) },
    demoToken
  );
  return res.data;
}

export async function publicDemoCloseSession(sessionId: string, demoToken: string): Promise<void> {
  await publicRequest(demos('sessions', sessionId, 'close'), { method: 'POST', body: '{}' }, demoToken);
}

export function demoTokenStorageKey(sessionId: string): string {
  return `demo_token:${sessionId}`;
}
